import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AthleteCard, { type AthleteCardTeam } from "@/components/AthleteCard";
import { Input } from "@/components/ui/input";
import { publicUrl } from "@/lib/storage";
import { athleteImageUrl } from "@/lib/athleteImage";
import type { Athlete } from "@/data/athletes";
import { athletes as seedAthletes } from "@/data/athletes";

import type { AthleteLinks } from "@/components/AthleteSocialLinks";

const Athletes = () => {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [dbIds, setDbIds] = useState<Record<string, string>>({});
  const [linksBySlug, setLinksBySlug] = useState<Record<string, AthleteLinks>>({});
  const [teamsBySlug, setTeamsBySlug] = useState<Record<string, AthleteCardTeam[]>>({});
  const [usingSeed, setUsingSeed] = useState(false);
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("All");
  const [team, setTeam] = useState("All");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("athletes")
        .select("*")
        .eq("published", true)
        .order("sort_order");
      const dbAthletes = data ?? [];
      if (dbAthletes.length === 0) {
        setAthletes(seedAthletes);
        setUsingSeed(true);
        setLoading(false);
        return;
      }
      const idMap: Record<string, string> = {};
      const linkMap: Record<string, AthleteLinks> = {};
      const slugById: Record<string, string> = {};
      const merged: Athlete[] = dbAthletes.map((a: any) => {
        idMap[a.slug] = a.id;
        slugById[a.id] = a.slug;
        linkMap[a.slug] = {
          instagram_url: a.instagram_url,
          tiktok_url: a.tiktok_url,
          facebook_url: a.facebook_url,
          youtube_url: a.youtube_url,
          linkedin_url: a.linkedin_url,
          custom_links: Array.isArray(a.custom_links) ? a.custom_links : [],
        };
        return {
          id: a.slug,
          name: a.name,
          sport: a.sport,
          country: a.country ?? "",
          image: athleteImageUrl(a.image_path),
          tagline: a.tagline ?? "",
          bio: a.bio ?? "",
          stats: Array.isArray(a.stats) ? (a.stats as any) : [],
          causes: [],
          brands: [],
        };
      });
      setAthletes(merged);
      setDbIds(idMap);
      setLinksBySlug(linkMap);
      setUsingSeed(false);
      setLoading(false);

      // Fetch team associations
      const athleteIds = dbAthletes.map((a: any) => a.id);
      const { data: links } = await supabase
        .from("athlete_teams")
        .select("athlete_id, team_id")
        .in("athlete_id", athleteIds);
      const teamIds = Array.from(new Set((links ?? []).map((l: any) => l.team_id)));
      if (teamIds.length === 0) return;
      const { data: teamRows } = await supabase
        .from("teams")
        .select("id, slug, name")
        .in("id", teamIds)
        .eq("published", true);
      const teamById: Record<string, AthleteCardTeam> = {};
      (teamRows ?? []).forEach((t: any) => {
        teamById[t.id] = { id: t.id, slug: t.slug, name: t.name };
      });
      const map: Record<string, AthleteCardTeam[]> = {};
      (links ?? []).forEach((l: any) => {
        const slug = slugById[l.athlete_id];
        const t = teamById[l.team_id];
        if (!slug || !t) return;
        (map[slug] ||= []).push(t);
      });
      setTeamsBySlug(map);
    })();
  }, []);

  const sports = useMemo(
    () => ["All", ...Array.from(new Set(athletes.map((a) => a.sport)))],
    [athletes],
  );

  const teamOptions = useMemo(() => {
    const names = new Set<string>();
    Object.values(teamsBySlug).forEach((ts) => ts.forEach((t) => names.add(t.name)));
    return ["All", ...Array.from(names).sort()];
  }, [teamsBySlug]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return athletes.filter((a) => {
      if (sport !== "All" && a.sport !== sport) return false;
      if (team !== "All") {
        const ts = teamsBySlug[a.id] ?? [];
        if (!ts.some((t) => t.name === team)) return false;
      }
      if (!s) return true;
      const teamHay = (teamsBySlug[a.id] ?? []).map((t) => t.name).join(" ");
      const hay = [a.name, a.sport, a.country, a.tagline, teamHay].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [athletes, q, sport, team, teamsBySlug]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-16">
        <header className="mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            The roster
          </span>
          <h1 className="text-5xl md:text-6xl mt-3">Meet the athletes.</h1>
          <p className="text-muted-foreground mt-3">
            Tap a card to read her story, support her cause, or shop her partners.
          </p>
        </header>

        <div className="flex flex-col gap-4 mb-8">
          <div className="max-w-md w-full">
            <Input
              type="search"
              placeholder="Search by name, sport, or team…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">Sport</p>
            <div className="flex flex-wrap gap-2">
              {sports.map((s) => (
                <button
                  key={s}
                  onClick={() => setSport(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    sport === s
                      ? "bg-secondary text-secondary-foreground border-secondary"
                      : "bg-background text-foreground border-border hover:border-secondary"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {teamOptions.length > 1 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">Team</p>
              <div className="flex flex-wrap gap-2">
                {teamOptions.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTeam(t)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      team === t
                        ? "bg-secondary text-secondary-foreground border-secondary"
                        : "bg-background text-foreground border-border hover:border-secondary"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden bg-card shadow-card">
                <div className="aspect-[3/4] bg-muted animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-muted rounded animate-pulse" />
                  <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">No athletes match your search.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {filtered.map((a) => (
              <AthleteCard
                key={a.id}
                athlete={a}
                favoriteId={dbIds[a.id]}
                hasProfile={!usingSeed && !!dbIds[a.id]}
                links={linksBySlug[a.id]}
                teams={teamsBySlug[a.id]}
              />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Athletes;
